import {Switch} from '@/components/commons/switch/Switch'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {Label} from '@/components/ui/label/Label'
import {Text} from '@/components/ui/text/Text'
import {AgendaEventProjectInfo} from '@/features/agenda/components/agenda-event-project-info/AgendaEventProjectInfo'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {InputSelectPatient} from '@/features/patients/components/input-select-patient/InputSelectPatient'
import {PatientsMultiSelect} from '@/features/patients/components/patients-multi-select/PatientsMultiSelect'
import {PatientSmall} from '@/features/patients/types'
import {InputSelectProjectByPatient} from '@/features/projects/components/input-select-project-by-patient/InputSelectProjectByPatient'
import {useTranslation} from '@/translations/i18n'
import {useCallback} from 'react'
import {useController, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const AgendaEventPatientsFieldsSchema = AgendaEventFieldsSchema.pick({
    category: true,
    patients: true,
    consultants: true,
    date: true,
    isGroupTreatment: true
})
type AgendaEventPatientsFieldsSchema = z.infer<typeof AgendaEventPatientsFieldsSchema>

export const AgendaEventPatientsFields = ({
    areDisabledPatientRelatedFields,
    mode
}: {
    areDisabledPatientRelatedFields: boolean
    mode?: 'create' | 'edit'
}) => {
    const {t} = useTranslation()
    const {control, setValue} = useFormContext<AgendaEventPatientsFieldsSchema>()
    const {
        fieldState: {error: patientsError}
    } = useController({control, name: 'patients'})
    const {field: isTreatmentGroupField} = useController({control, name: 'isGroupTreatment'})
    const category = useWatch({control, name: 'category'})

    const {fields, replace, remove} = useFieldArray({
        control,
        name: 'patients',
        keyName: 'key'
    })

    const handleUpdatePatient = useCallback(
        (
            patients: Pick<
                PatientSmall,
                'id' | 'fullName' | 'treatmentRequestId' | 'project' | 'neuromuscularDiagnosisId'
            >[]
        ) => {
            replace(
                patients.map(patient => ({
                    ...patient,
                    project: patient.project && !patient.project.isSuspended ? patient.project : null
                }))
            )
        },
        [replace]
    )

    const date = useWatch({control, name: 'date.date'})

    return (
        <>
            {!areDisabledPatientRelatedFields && category == 'treatments' && (
                <Switch
                    label={t('agenda:group_treatment')}
                    size="sm"
                    {...isTreatmentGroupField}
                    checked={!!isTreatmentGroupField.value}
                    disabled={mode == 'edit'}
                    onChange={value => {
                        isTreatmentGroupField.onChange(value)
                        setValue('consultants', [{id: '', fullName: ''}])
                        if (!value) {
                            handleUpdatePatient([])
                        }
                    }}
                />
            )}
            <Flexbox direction="column" gap={4} fullWidth>
                <Grid columns={2} gap={4} fullWidth>
                    <GridItem direction="column" gap={1.5}>
                        <Label>{isTreatmentGroupField.value ? t('commons:patients') : t('commons:patient')}</Label>
                        {isTreatmentGroupField.value ? (
                            <PatientsMultiSelect
                                placeholder={t('agenda:add_patient_to_group')}
                                values={fields}
                                onChange={handleUpdatePatient}
                                errorMessage={patientsError?.message}
                            />
                        ) : (
                            <InputSelectPatient
                                placeholder={t('commons:select_patient')}
                                value={fields[0]}
                                hideBadge
                                errorMessage={patientsError?.message}
                                onChange={patient => {
                                    patient && handleUpdatePatient([patient])
                                }}
                            />
                        )}
                    </GridItem>

                    {!isTreatmentGroupField.value && fields.length > 0 && (
                        <GridItem>
                            {fields[0].project && (
                                <InputSelectProjectByPatient
                                    patientId={fields[0]?.id}
                                    label={t('commons:projects', {count: 1})}
                                    placeholder={t('projects:search_project')}
                                    date={date}
                                    onChange={project => {
                                        handleUpdatePatient(fields.map(patient => ({...patient, project})))
                                    }}
                                    value={
                                        fields[0]?.project
                                            ? {
                                                  id: fields[0].project.id,
                                                  code: fields[0].project.code,
                                                  startDate: fields[0].project.startDate,
                                                  expirationDate: fields[0].project.expirationDate
                                              }
                                            : null
                                    }
                                    disableClosedProjects
                                />
                            )}
                        </GridItem>
                    )}
                </Grid>

                {isTreatmentGroupField.value && fields.length > 0 && (
                    <Grid columns={2} gap={4} fullWidth>
                        {fields.map((item, index) => {
                            return <AgendaEventPatientItem key={item.id} value={item} remove={() => remove(index)} />
                        })}
                    </Grid>
                )}
            </Flexbox>
        </>
    )
}

export const AgendaEventPatientItem = ({
    value,
    remove
}: {
    value: Pick<PatientSmall, 'fullName' | 'project' | 'treatmentRequestId'>
    remove: () => void
}) => {
    return (
        <GridItem gap={1.5} fullWidth>
            <Flexbox direction="column" fullWidth gap={1}>
                <Text size="textSm" weight="500" palette="neutral" shade="500">
                    {value.fullName}
                </Text>
                <AgendaEventProjectInfo showProjectLabel patientName={value.fullName} project={value.project} />
            </Flexbox>
            <Button shape="square" onClick={remove} size="sm" variant="ghost">
                <XCloseIcon />
            </Button>
        </GridItem>
    )
}
